
import React from 'react';
import { AnalysisResult, COLORS } from '../types';
import { createImpactPieData } from '../utils';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from 'recharts';
import CustomTooltip from '../CustomTooltip';
import { Users, Handshake, TrendingUp } from 'lucide-react';

type ExecutionApproachTabProps = {
  analysisResult: AnalysisResult;
};

const ExecutionApproachTab = ({ analysisResult }: ExecutionApproachTabProps) => {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="bg-secondary/30 rounded-lg p-6 border border-primary/10 lg:col-span-3">
        <h4 className="text-lg font-semibold text-primary mb-4">Execution Strategy Overview</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-secondary/50 rounded-lg p-4 border border-purple-900/20">
            <div className="flex items-center gap-2 mb-4">
              <Users className="h-5 w-5 text-purple-400" />
              <h5 className="font-medium text-primary">Stakeholder Strategy</h5>
            </div>
            <p className="text-white/80 text-sm">
              {analysisResult.executionApproach?.stakeholderStrategy}
            </p>
          </div>
          
          <div className="bg-secondary/50 rounded-lg p-4 border border-blue-900/20">
            <div className="flex items-center gap-2 mb-4">
              <Handshake className="h-5 w-5 text-blue-400" />
              <h5 className="font-medium text-primary">Communication Plan</h5>
            </div>
            <p className="text-white/80 text-sm">
              {analysisResult.executionApproach?.communicationPlan}
            </p>
          </div>
          
          <div className="bg-secondary/50 rounded-lg p-4 border border-indigo-900/20">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="h-5 w-5 text-indigo-400" />
              <h5 className="font-medium text-primary">Value Proposition</h5>
            </div>
            <p className="text-white/80 text-sm">
              {analysisResult.executionApproach?.valueProposition}
            </p>
          </div>
        </div>
      </div>
      
      <div className="bg-secondary/30 rounded-lg p-6 border border-primary/10 lg:col-span-2">
        <h4 className="text-lg font-semibold text-primary mb-4">KPI Impact Analysis</h4>
        <p className="text-white/80 mb-6 text-sm">
          {analysisResult.kpiImpactAnalysis?.description}
        </p>
        <div className="h-60">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={createImpactPieData(analysisResult)}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(Number(percent) * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {createImpactPieData(analysisResult).map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip 
                content={<CustomTooltip />}
                formatter={(value) => [`${(Number(value) * 100).toFixed(0)}%`, 'Impact Percentage']}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
      
      <div className="bg-secondary/30 rounded-lg p-6 border border-primary/10">
        <h4 className="text-lg font-semibold text-primary mb-4">Top KPIs Addressed</h4>
        <div className="space-y-2">
          {analysisResult.kpiImpactAnalysis?.topKpis.map((kpi, index) => (
            <div 
              key={index} 
              className="flex items-center gap-2 p-2 bg-secondary/50 rounded border border-primary/10"
            >
              <div 
                className="w-3 h-3 rounded-full" 
                style={{ backgroundColor: COLORS[index % COLORS.length] }}
              />
              <span className="text-white text-sm">{kpi}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ExecutionApproachTab;
